import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { isValidPhoneNumber } from "libphonenumber-js";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ConfirmationIcon } from "../../../../assets/icons/Confirmation.svg";
import { commonStyles } from "../../../../assets/MUIStyles/commonStyles";
import { layoutStyles } from "../../../../assets/MUIStyles/mainLayout";
import BaseButton from "../../Common/Buttons/BaseButton";
import ErrorPopUp from "../../CommonComponents/PopUpCards/ErrorPopUp";

const UpdateProfile = ({
  open,
  formData,
  setFormData,
  handleModalClose,
  updateUserDetails,
}) => {
  const theme = useTheme();
  const brandColor = theme.custom.brandColor;
  const [isVisible, setIsVisible] = useState(true);
  const [updateButtonLoading, setUpdateButtonLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { firstName, lastName, phone, email, address, city, pincode } =
    formData;

  const [warningPopup, setWarningPopup] = useState({
    open: false,
    errMsg: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["phone", "pincode"].includes(name)) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value.replace(/[a-zA-Z]/g, ""),
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const checkWhiteSpace = (str) => !str || !str.trim();

  const handleSubmit = async () => {
    setUpdateButtonLoading(true);

    const obj = {
      firstName,
      lastName,
      phone,
      email,
      city,
      address,
      pincode,
    };

    for (let key in obj) {
      if (checkWhiteSpace(obj[key])) {
        setWarningPopup({
          open: true,
          errMsg: "All fields are required.",
        });
        setUpdateButtonLoading(false);
        return;
      }
    }

    if (!isValidPhoneNumber(phone)) {
      setWarningPopup({
        open: true,
        errMsg: "Please enter a valid phone number with country code.",
      });
      setUpdateButtonLoading(false);
      return;
    }

    await updateUserDetails(formData);
    setUpdateButtonLoading(false);
  };

  const handleClose = () => {
    setIsVisible(false);
    handleModalClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (warningPopup.open) {
        return;
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [warningPopup.open]);

  return createPortal(
    <Box
      ref={containerRef}
      sx={{
        ...layoutStyles.profileUpdateContainer,
        left: open ? "280px" : "90px",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
      }}
    >
      <Box sx={layoutStyles.profileInnerContainer}>
        <Avatar
          sx={{ ...layoutStyles.profileAvatar, backgroundColor: brandColor }}
        >
          {formData.email?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Typography className="font-16-600-grey">
          {formData.firstName} {formData.lastName}
        </Typography>
        <Typography className="font-16-grey" gutterBottom>
          {formData.email}
        </Typography>
      </Box>
      <Box sx={layoutStyles.profileContentContainer}>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">First Name</Typography>
            <TextField
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your first name"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">Last Name</Typography>
            <TextField
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your last name"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 25 },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Email</Typography>
          <TextField
            name="email"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="mailid@gmail.com"
            sx={commonStyles.inputField}
            disabled
            slotProps={{
              htmlInput: { maxLength: 50 },
            }}
          />
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Phone number</Typography>
          <TextField
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your Phone Number"
            sx={commonStyles.inputField}
            slotProps={{
              htmlInput: { maxLength: 18 },
            }}
          />
        </Box>
        <Box
          sx={{
            mb: 1,
          }}
        >
          <Typography className="font-14-gery mb-1">Address</Typography>
          <TextField
            name="address"
            value={formData.address}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your address here"
            sx={commonStyles.inputField}
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">City</Typography>
            <TextField
              name="city"
              value={formData.city}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your city"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 35 },
              }}
            />
          </Box>
          <Box
            sx={{
              width: 1 / 2,
              mb: 1,
            }}
          >
            <Typography className="font-14-gery mb-1">Pincode</Typography>
            <TextField
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              placeholder="Enter your Pincode"
              sx={commonStyles.inputField}
              slotProps={{
                htmlInput: { maxLength: 10 },
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={layoutStyles.profileButtonCont}>
        <Button onClick={handleClose} sx={commonStyles.clearButton}>
          Close
        </Button>
        <BaseButton
          variant="contained"
          title="Update profile"
          loading={updateButtonLoading}
          handleClick={handleSubmit}
        />
      </Box>
      {warningPopup.open && (
        <ErrorPopUp
          show={warningPopup.open}
          headerText="Warning"
          toggle={() =>
            setWarningPopup((prev) => ({
              ...prev,
              open: false,
              errMsg: "",
            }))
          }
          heading={"Warning"}
          message={warningPopup.errMsg}
          spin={false}
          Icon={ConfirmationIcon}
        />
      )}
    </Box>,
    document.body
  );
};

export default UpdateProfile;
